/**
 * DefaultGate (TRA-1368): whether a run's served step may become the
 * team's default model, under the run's Outcome. Everyone on the team
 * sees the verdict of the run's held-out eval and the tests it scored
 * lower on. Staff on their own team also get "Make default", which the
 * api refuses unless the verdict is eligible, and "Score again", which
 * starts another held-out eval of the run.
 */

import { useState } from 'react'

import { getApiErrorDetail } from '@/api/client'
import type { TrainingGateTask, TrainingHeldOutEval } from '@/api/generated/model'
import { ConfirmDialog } from '@/components/shared/ConfirmDialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useModels } from '@/hooks/useModels'
import {
  useAwaitsHeldOutEval,
  useMakeRunDefault,
  useScoreHeldOutEval,
} from '@/hooks/useTrainingRuns'

import { GATE_LABEL, GATE_VARIANT, gateLines, gateState, isServedModel } from './gateVerdict'

interface DefaultGateProps {
  runId: string
  heldOut: TrainingHeldOutEval
  /** Staff on a team they belong to; the api 403s everyone else regardless. */
  canApprove: boolean
}

export function DefaultGate({ runId, heldOut, canApprove }: DefaultGateProps): React.JSX.Element {
  const models = useModels()
  const makeDefault = useMakeRunDefault()
  const [confirming, setConfirming] = useState(false)
  const gate = heldOut.gate ?? null
  const isDefault = (models.data ?? []).some(
    (model) => model.is_default && isServedModel(model, heldOut),
  )
  const state = gateState(gate, isDefault)
  const lines = isDefault ? ["This model is the team's default."] : gateLines(gate)
  return (
    <section aria-labelledby="default-gate-title" className="space-y-3 border border-border p-4">
      <div className="flex flex-wrap items-center gap-3">
        <h3 id="default-gate-title" className="font-serif text-base text-foreground">
          Default model
        </h3>
        <Badge variant={GATE_VARIANT[state]}>{GATE_LABEL[state]}</Badge>
        <span className="ml-auto break-all font-mono text-xs text-muted-foreground">
          {heldOut.model_slug}
        </span>
      </div>
      {lines.map((line) => (
        <p key={line} className="text-sm text-muted-foreground">
          {line}
        </p>
      ))}
      {gate !== null && gate.lower.length > 0 && <LowerTests tests={gate.lower} />}
      {canApprove && (
        <div className="flex flex-wrap items-center gap-3">
          {state === 'eligible' && (
            <Button
              onClick={() => {
                makeDefault.reset()
                setConfirming(true)
              }}
            >
              Make default
            </Button>
          )}
          <ScoreHeldOutButton runId={runId} label="Score again" scoredAt={heldOut.scored_at} />
        </div>
      )}
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title="Make this model the default"
        description={
          <>
            The nightly evals will score{' '}
            <span className="break-all font-mono text-xs">{heldOut.model_slug}</span> from the next
            night. The current default model stays on the Models page.
          </>
        }
        confirmLabel="Make default"
        pendingLabel="Making default..."
        isPending={makeDefault.isPending}
        error={
          makeDefault.isError
            ? (getApiErrorDetail(makeDefault.error) ?? 'The default was not changed. Try again')
            : null
        }
        onConfirm={() => {
          makeDefault.mutate(
            { runId },
            {
              onSuccess: () => {
                setConfirming(false)
              },
            },
          )
        }}
      />
    </section>
  )
}

/**
 * Staff-only: start a held-out eval of the run, the first or another.
 * `scoredAt` is when the run's eval on the page was scored, null for a run
 * never scored. Once started, the status line stays until the page reads
 * a newer result (the runs are polled for it, `useTrainingRuns`) or the
 * eval's Job can no longer be running, and the button is back for the
 * next scoring.
 */
export function ScoreHeldOutButton({
  runId,
  label,
  scoredAt,
}: {
  runId: string
  label: string
  scoredAt: string | null
}): React.JSX.Element {
  const score = useScoreHeldOutEval()
  const awaited = useAwaitsHeldOutEval(runId, scoredAt)
  if (awaited) {
    // Plain text, not a disabled control: it stays readable and focus-free.
    return (
      <span role="status" className="text-xs text-muted-foreground">
        The held-out eval started. Its result shows here when it finishes.
      </span>
    )
  }
  return (
    <Button
      variant="outline"
      disabled={score.isPending}
      onClick={() => {
        score.mutate({ runId })
      }}
    >
      {label}
    </Button>
  )
}

/** The compared tests the model scored lower on, the largest drop first. */
function LowerTests({ tests }: { tests: TrainingGateTask[] }): React.JSX.Element {
  const caption =
    tests.length === 1 ? 'Lower on 1 test' : `Lower on ${tests.length.toLocaleString()} tests`
  return (
    <div className="max-h-56 overflow-auto border border-border">
      <table className="w-full text-left text-sm">
        <caption className="sr-only">{caption}</caption>
        <thead>
          <tr className="border-b border-border font-mono text-xs uppercase tracking-wider text-muted-foreground">
            <th scope="col" className="px-3 py-2 font-normal">
              {caption}
            </th>
            <th scope="col" className="px-3 py-2 text-right font-normal">
              Default
            </th>
            <th scope="col" className="px-3 py-2 text-right font-normal">
              This model
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {tests.map((test) => (
            <tr key={test.task}>
              <td className="break-all px-3 py-2 font-mono text-xs text-foreground">{test.task}</td>
              <td className="px-3 py-2 text-right font-mono text-xs text-foreground">
                {test.baseline.toLocaleString()}
              </td>
              <td className="px-3 py-2 text-right font-mono text-xs text-foreground">
                {test.score.toLocaleString()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
